import { useEffect, useMemo, useState } from "react";
import { kindLabel } from "@/lib/leadKind";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const TONE = { opened: "bg-emerald-50 text-emerald-800", sent: "bg-slate-100 text-slate-700", bounced: "bg-red-50 text-red-700", failed: "bg-amber-50 text-amber-800" };
const when = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const state = (e) => (e.status === "bounced" || e.status === "failed" ? e.status : e.opened_at || e.clicked_at ? "opened" : "sent");

const Row = ({ e }) => {
  const s = state(e);
  return (
    <div className="grid gap-2 border-t border-slate-100 py-3 text-sm md:grid-cols-[1.1fr_2fr_2.4fr_1.4fr]" data-testid={`pm-email-${e.id}`}>
      <div className="text-slate-700">{when(e.sent_at)}</div>
      <div><div className="font-medium text-slate-900">{e.name || e.to}</div><div className="break-all text-xs text-slate-500">{e.to}</div></div>
      <div className="text-slate-700"><div className="line-clamp-2">{e.subject}</div>
        <div className="text-xs text-slate-500">{kindLabel(e.kind)}{e.cc ? ` · CC ${e.cc}` : ""}</div></div>
      <div>
        <Badge className={`border-0 capitalize ${TONE[s]}`} data-testid={`pm-email-status-${e.id}`}>{s}</Badge>
        <div className="mt-1 text-xs text-slate-500">
          {s === "opened" && e.opened_at && `First opened ${when(e.opened_at)}${e.opens > 1 ? ` · ${e.opens} opens` : ""}`}
          {e.clicked_at && <div className="font-medium text-emerald-700" data-testid={`pm-email-clicked-${e.id}`}>
            Clicked the video {when(e.clicked_at)}{e.clicks > 1 ? ` · ${e.clicks} clicks` : ""}</div>}
          {s === "bounced" && `Bounced ${when(e.bounced_at)} · a postcard goes out instead`}
          {s === "failed" && (e.error || "Could not send")}
        </div>
      </div>
    </div>
  );
};

const MailRow = ({ p }) => (
  <div className="grid gap-2 border-t border-slate-100 py-3 text-sm md:grid-cols-[1.1fr_2fr_2.4fr_1.4fr]" data-testid={`pm-mail-${p.id}`}>
    <div className="text-slate-700">{when(p.sent_at)}</div>
    <div><div className="font-medium text-slate-900">{p.name}</div><div className="text-xs text-slate-500">{p.address}</div></div>
    <div className="text-slate-700"><div>Postcard</div><div className="text-xs text-slate-500">{kindLabel(p.kind)}</div></div>
    <div>
      <Badge className={`border-0 ${p.status === "failed" ? TONE.failed : p.scanned ? TONE.opened : TONE.sent}`} data-testid={`pm-mail-status-${p.id}`}>
        {p.status === "failed" ? "Failed" : p.scanned ? "QR scanned" : "Mailed"}</Badge>
      <div className="mt-1 text-xs text-slate-500">
        {p.status === "failed" && (p.error || "Could not mail")}
        {p.scanned && `${p.scans > 1 ? `${p.scans} scans · last ` : "Scanned "}${when(p.last_scan)}`}
      </div>
    </div>
  </div>
);

export default function PmEmails() {
  const [rows, setRows] = useState(null);
  const [mail, setMail] = useState([]);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/pm/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/pm/emails").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/pm/postcards").then((r) => setMail(r.data)).catch(() => {});
  }, []);
  const shownMail = useMemo(() => mail.filter((p) => !q || `${p.name} ${p.address}`.toLowerCase().includes(q.toLowerCase())), [mail, q]);
  const shown = useMemo(() => (rows || []).filter((e) => (kind === "all" || e.kind === kind || (kind === "clicked" && e.clicked_at))
    && (!q || `${e.name} ${e.to} ${e.subject}`.toLowerCase().includes(q.toLowerCase()))), [rows, kind, q]);
  const count = (s) => (rows || []).filter((e) => state(e) === s).length;
  const clicked = (rows || []).filter((e) => e.clicked_at).length;
  return (
    <div className="mx-auto max-w-6xl" data-testid="pm-emails-page">
      <div className="mb-5">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Email &amp; mail log</h1>
        <p className="text-sm text-slate-600">Every email and postcard sent to your leads: emails opened or bounced, postcards mailed and QR codes scanned. You're CCed on each email, so opening your own copy or clicking its video link also counts.</p>
      </div>
      {stats && <div className="mb-4 grid grid-cols-2 gap-3" data-testid="pm-stats">
        <Card className="p-4"><div className="text-xs text-slate-500">Emails viewed</div>
          <div className="text-2xl font-bold text-slate-900" data-testid="pm-stats-emails-viewed">{stats.emails_viewed} <span className="text-sm font-medium text-slate-500">of {stats.emails_sent}</span></div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500">Postcard QR codes scanned</div>
          <div className="text-2xl font-bold text-slate-900" data-testid="pm-stats-qr-scans">{stats.postcard_qr_scans} <span className="text-sm font-medium text-slate-500">of {stats.postcards_sent} mailed</span></div></Card>
      </div>}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        {[["Sent", (rows || []).length - count("failed"), "total"], ["Opened", count("opened"), "opened"], ["Clicked video", clicked, "clicked"], ["Not opened yet", count("sent"), "unopened"], ["Bounced", count("bounced"), "bounced"]].map(([l, n, id]) => (
          <Card key={id} className="p-4"><div className="text-xs text-slate-500">{l}</div><div className="text-2xl font-bold text-slate-900" data-testid={`pm-emails-count-${id}`}>{n}</div></Card>
        ))}
      </div>
      <Card className="p-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <Tabs value={kind} onValueChange={setKind} className="max-w-full overflow-x-auto"><TabsList>
            <TabsTrigger value="all" data-testid="pm-emails-tab-all">All</TabsTrigger>
            <TabsTrigger value="owner" data-testid="pm-emails-tab-owner">Property owners</TabsTrigger>
            <TabsTrigger value="agent" data-testid="pm-emails-tab-agent">Elite Rentals</TabsTrigger>
          <TabsTrigger value="clicked" data-testid="pm-emails-tab-clicked">Clicked video</TabsTrigger>
          <TabsTrigger value="mail" data-testid="pm-emails-tab-mail">Postcards</TabsTrigger>
          </TabsList></Tabs>
          <Input className="max-w-xs" placeholder="Search name, email, address or subject" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pm-emails-search" />
        </div>
        {kind === "mail" ? <>
          {shownMail.map((p) => <MailRow key={p.id} p={p} />)}
          {!shownMail.length && <p className="py-6 text-center text-sm text-slate-500" data-testid="pm-mail-empty">No postcards mailed yet.</p>}
        </> : <>
        {shown.map((e) => <Row key={e.id} e={e} />)}
        {!shown.length && <p className="py-6 text-center text-sm text-slate-500" data-testid="pm-emails-empty">
          {rows === null ? "Loading..." : rows.length ? "No matching emails." : "No emails yet."}</p>}
        </>}
      </Card>
    </div>
  );
}
